import React, {useEffect, useRef} from "react";
import '../../styles/components/keyboard/keyboard.scss';
import {createImageElementsAndWaitForThemToLoad, createKeyboardLayoutModel} from "../../factories/keyboardFactory";
import {
  emitUiKeyboardNote_pressed,
  emitUiKeyboardNote_released,
  subscribeToNotesEvents
} from "../../services/eventBus/notes/notesEventBus";
import {NotesEventTypes} from "../../services/eventBus/notes/NotesEventTypes";
import Note from "../../models/soundslice/Note";
import {IPianoKey} from "../../models/view/keyboard/IPianoKey";
import {drawKeys, drawKeysHighlights, drawNoteNameLabels} from "./keys";
import Fingering from "../../models/view/lessonPage/Fingering";
import settingsService from "../../services/settings/settingsService";
import {logger} from "../../services/log/logger";
import {useAppSelector} from "../../redux/hooks";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {settingsStore} from "../../redux/settings/settingsStore";

interface ITouchIdToMidiNoteHash {
  [touchId: number] : number;
}

interface ICanvases {
  whiteKeysLabelsAndImagesCanvas: HTMLCanvasElement|null;
  whiteKeysHighlightsCanvas: HTMLCanvasElement|null;
  playerNotesWhiteKeysHighlightsCanvas: HTMLCanvasElement|null;
  blackKeysLabelsAndImagesCanvas: HTMLCanvasElement|null;
  blackKeysHighlightsCanvas: HTMLCanvasElement|null;
  playerNotesBlackKeysHighlightsCanvas: HTMLCanvasElement|null;
}

type CanvasRef = React.MutableRefObject<HTMLCanvasElement>;

/**
 * Keyboard uses several canvases (for performance) to paint the finger markers for the notes currently being played.
 *
 * Listens for rxjs note events emitted by the notesEventWorker and playerNotesPlayer in order to have the keyboard UI reflect player notes, sheet music notes, currently selected notes, etc.
 *
 * The canvases are stacked, in order, on top of each other.
 * We use 6 canvases to simplify things and increase performance.
 * 1. white key images canvas for drawing the actual image of the key. This won't need to be rerendered on updates to notes being played by sheet music or the player.
 * 2. white key highlights to place a gradient rectangle over the key image so we can indicate it's being played. By having this as the second layer, we don't need to worry about covering part of the black key image with the highlight (white key shape is not a rectangle, so we'd need to do some calculations to trace its shape)
 * 3. player white key highlights for player notes. we don't want player note highlights to get erased when sheet music notes change.
 * 4-6. same as 1-3, but for black keys.
 */
export default function Keyboard() {
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  const fingerings = segment?.fingerings;
  const keyboardKeysSettings = settingsService.getKeyboardKeysSettings();
  const userKeyboardLowestMidiNumber = keyboardKeysSettings.lowestNote;
  const userKeyboardHighestMidiNumber = keyboardKeysSettings.highestNote;
  //create refs. use new canvas to avoid having to constantly check for nulls.
  const whiteKeysLabelsAndImagesCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const whiteKeysHighlightsCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const playerNotesWhiteKeysHighlightsCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const blackKeysLabelsAndImagesCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const blackKeysHighlightsCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const playerNotesBlackKeysHighlightsCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));

  // Use a ref so the value isn't reset back to an empty array on each render.
  const pianoKeysRef = useRef<IPianoKey[]>([]);
  //on first render, size and draw the canvases.  listen for sheetMusicNotesCurrentlyBeingPlayedChanged rxjs events and repaint the finger markers.
  useEffect(()=> {
    //keep a reference to the notes currently being played, as we will need to repaint them when screen resize occurs.
    let sheetMusicNotesCurrentlyBeingPlayed: Note[] = [];
    let notesCurrentlySelected: Note[] = [];
    //function for initial render and rerender
    function calculateSizesAndRender(){
      const canvases = getCanvasesFromRefs({whiteKeysLabelsAndImagesCanvasRef, whiteKeysHighlightsCanvasRef, playerNotesWhiteKeysHighlightsCanvasRef, blackKeysLabelsAndImagesCanvasRef, blackKeysHighlightsCanvasRef, playerNotesBlackKeysHighlightsCanvasRef});
      pianoKeysRef.current = setCanvasSizeCreatePianoKeysAndRender(
        canvases,
        fingerings,
        sheetMusicNotesCurrentlyBeingPlayed,
        notesCurrentlySelected,
        userKeyboardHighestMidiNumber,
        userKeyboardLowestMidiNumber
      );
    }
    //initial render
    calculateSizesAndRender();
    //rerender on window size changes
    const unsubscribeFromResizeAndOrientationChangeEvents = listenForWindowResizeAndOrientationChangeEvents(calculateSizesAndRender);
    //listen for player notes
    const notesCurrentlyBeingPlayedByUser = new Set<number>();
    const unsubscribeFromNotesEvents = subscribeToNotesEvents((e) => {
      const canvases = getCanvasesFromRefs({
        whiteKeysLabelsAndImagesCanvasRef,
        whiteKeysHighlightsCanvasRef,
        playerNotesWhiteKeysHighlightsCanvasRef,
        blackKeysLabelsAndImagesCanvasRef,
        blackKeysHighlightsCanvasRef,
        playerNotesBlackKeysHighlightsCanvasRef
      });

      handleNoteEvent(
        e,
        notesCurrentlyBeingPlayedByUser,
        pianoKeysRef.current,
        sheetMusicNotesCurrentlyBeingPlayed,
        notesCurrentlySelected,
        canvases
      );
    });

    //return a function for unsubscribing.
    return () =>{
      unsubscribeFromResizeAndOrientationChangeEvents();
      unsubscribeFromNotesEvents();
    };
  }, [
    /**
     * Reloads the on-screen keyboard when the user completes the keyboard size setup.
     * This is not ideal. The keyboard component should be coupled to whether a modal is displayed or not.
     * Ideally, the keyboard setup would be in state (e.g. Redux), and this component would subscribe to that.
     */
    userKeyboardHighestMidiNumber,
    userKeyboardLowestMidiNumber,
    fingerings,
  ]);


  const isTouchDevice = ('ontouchstart' in window);

  const activeMouseDownMidiNotes = new Set<number>();
  const onMouseDown = !isTouchDevice ? (e: React.MouseEvent<HTMLDivElement>) => handleMouseDown(e, pianoKeysRef.current, playerNotesWhiteKeysHighlightsCanvasRef.current, activeMouseDownMidiNotes) : undefined;
  const onMouseUp = !isTouchDevice ? () => handleMouseUp(playerNotesWhiteKeysHighlightsCanvasRef.current, pianoKeysRef.current, activeMouseDownMidiNotes) : undefined;

  const activeTouches: ITouchIdToMidiNoteHash = {};
  const onTouchStart = isTouchDevice ? (e: React.TouchEvent) => handleTouchStart(e, pianoKeysRef.current, playerNotesWhiteKeysHighlightsCanvasRef.current, activeTouches) : undefined;
  const onTouchEnd = isTouchDevice ? (e: React.TouchEvent) => handleTouchEnd(e, pianoKeysRef.current, playerNotesWhiteKeysHighlightsCanvasRef.current, activeTouches): undefined;
  const onTouchMove = isTouchDevice ? (e: React.TouchEvent) => handleTouchMove(e, pianoKeysRef.current, playerNotesWhiteKeysHighlightsCanvasRef.current, activeTouches): undefined;

  return (
    <div
      className="keyboard"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
      onTouchCancel={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseOut={onMouseUp}
      onMouseLeave={onMouseUp}>
      <div className={`top`}>&nbsp;</div>
      <canvas ref={whiteKeysLabelsAndImagesCanvasRef}/>
      <canvas ref={whiteKeysHighlightsCanvasRef}/>
      <canvas ref={playerNotesWhiteKeysHighlightsCanvasRef}/>

      <canvas ref={blackKeysLabelsAndImagesCanvasRef}/>
      <canvas ref={blackKeysHighlightsCanvasRef}/>
      <canvas ref={playerNotesBlackKeysHighlightsCanvasRef}/>
    </div>
  );
}

function getCanvasesFromRefs({whiteKeysLabelsAndImagesCanvasRef, whiteKeysHighlightsCanvasRef, playerNotesWhiteKeysHighlightsCanvasRef, blackKeysLabelsAndImagesCanvasRef, blackKeysHighlightsCanvasRef, playerNotesBlackKeysHighlightsCanvasRef, } :{whiteKeysLabelsAndImagesCanvasRef: CanvasRef, whiteKeysHighlightsCanvasRef: CanvasRef, playerNotesWhiteKeysHighlightsCanvasRef: CanvasRef, blackKeysLabelsAndImagesCanvasRef: CanvasRef, blackKeysHighlightsCanvasRef: CanvasRef, playerNotesBlackKeysHighlightsCanvasRef: CanvasRef, }): ICanvases {
  return {
    whiteKeysLabelsAndImagesCanvas: whiteKeysLabelsAndImagesCanvasRef.current,
    whiteKeysHighlightsCanvas: whiteKeysHighlightsCanvasRef.current,
    playerNotesWhiteKeysHighlightsCanvas: playerNotesWhiteKeysHighlightsCanvasRef.current,
    blackKeysLabelsAndImagesCanvas: blackKeysLabelsAndImagesCanvasRef.current,
    blackKeysHighlightsCanvas: blackKeysHighlightsCanvasRef.current,
    playerNotesBlackKeysHighlightsCanvas: playerNotesBlackKeysHighlightsCanvasRef.current
  };
}

function setCanvasSizeCreatePianoKeysAndRender(canvases: ICanvases, fingerings: Fingering[] | undefined, sheetMusicNotesCurrentlyBeingPlayed: Note[], notesCurrentlySelected: Note[], userKeyboardHighestMidiNumber: number, userKeyboardLowestMidiNumber: number){
  const {whiteKeysLabelsAndImagesCanvas, whiteKeysHighlightsCanvas, blackKeysLabelsAndImagesCanvas, blackKeysHighlightsCanvas, playerNotesWhiteKeysHighlightsCanvas, playerNotesBlackKeysHighlightsCanvas,} = canvases;
  if (!whiteKeysLabelsAndImagesCanvas || !whiteKeysHighlightsCanvas || !blackKeysLabelsAndImagesCanvas || !blackKeysHighlightsCanvas || !playerNotesWhiteKeysHighlightsCanvas || !playerNotesBlackKeysHighlightsCanvas) {
    return [];
  }
  setCanvasSizesToMatchCSS([whiteKeysLabelsAndImagesCanvas, whiteKeysHighlightsCanvas, blackKeysLabelsAndImagesCanvas, blackKeysHighlightsCanvas, playerNotesWhiteKeysHighlightsCanvas, playerNotesBlackKeysHighlightsCanvas, ]);
  const pianoKeys = createKeyboardLayoutModel(
    {
      width: whiteKeysLabelsAndImagesCanvas.width,
      height: whiteKeysLabelsAndImagesCanvas.height,
      userKeyboardHighestMidiNumber,
      userKeyboardLowestMidiNumber,
      fingerings
    });
  drawKeyAndLabelAndHighlightCanvases(
    whiteKeysHighlightsCanvas,
    whiteKeysLabelsAndImagesCanvas,
    blackKeysHighlightsCanvas,
    blackKeysLabelsAndImagesCanvas,
    sheetMusicNotesCurrentlyBeingPlayed,
    notesCurrentlySelected,
    pianoKeys
  );
  return pianoKeys;
}

function handleNoteEvent(e: NotesEventTypes, notesCurrentlyBeingPlayedByUser: Set<number>, pianoKeys: IPianoKey[], sheetMusicNotesCurrentlyBeingPlayed: Note[], notesCurrentlySelected: Note[], canvases: ICanvases){
  const {playerNotesWhiteKeysHighlightsCanvas, playerNotesBlackKeysHighlightsCanvas, whiteKeysHighlightsCanvas, blackKeysHighlightsCanvas} = canvases;

  switch(e.type) {
    case "playerNotePressed":
      const rawMidiDeviceId = e.deviceId;

      // selectedMidiDeviceId corresponds to an actual midi input device whose value has been normalized.

      // Make sure note came from the selected midi input device or the virtual keyboard
      if ((e.normalizedMidiInput?.getId() !== settingsStore.getSelectedMidiDeviceId()) && (rawMidiDeviceId !== "virtualKeyboard")) {
        logger.midi.info({
          message: `Keyboard: Note detected from wrong device. Ignoring. Device expected: ${settingsStore.getSelectedMidiDeviceId()}, device found: ${e.normalizedMidiInput?.getId()}`,
        });
        return;
      }

      notesCurrentlyBeingPlayedByUser.add(e.midiNumber);
      if (playerNotesWhiteKeysHighlightsCanvas && playerNotesBlackKeysHighlightsCanvas) {
        drawPlayerNoteHighlights(notesCurrentlyBeingPlayedByUser, pianoKeys, playerNotesWhiteKeysHighlightsCanvas, playerNotesBlackKeysHighlightsCanvas);
      }
      break;
    case "playerNoteReleased":
      notesCurrentlyBeingPlayedByUser.delete(e.midiNumber);
      if (playerNotesWhiteKeysHighlightsCanvas && playerNotesBlackKeysHighlightsCanvas) {
        drawPlayerNoteHighlights(notesCurrentlyBeingPlayedByUser, pianoKeys, playerNotesWhiteKeysHighlightsCanvas, playerNotesBlackKeysHighlightsCanvas);
      }
      break;
    case 'sheetMusicNotesCurrentlyBeingPlayedChanged':
      sheetMusicNotesCurrentlyBeingPlayed = e.sheetMusicNotesCurrentlyBeingPlayed;
      if (whiteKeysHighlightsCanvas && blackKeysHighlightsCanvas) {
        onSheetMusicNotesCurrentlyBeingPlayed_changed(pianoKeys, sheetMusicNotesCurrentlyBeingPlayed, whiteKeysHighlightsCanvas, blackKeysHighlightsCanvas);
      }
      break;
    case 'notesCurrentlySelectedChanged':
      notesCurrentlySelected = e.notesCurrentlySelected;
      if (whiteKeysHighlightsCanvas && blackKeysHighlightsCanvas) {
        onNotesCurrentlySelected_changed(pianoKeys, notesCurrentlySelected, whiteKeysHighlightsCanvas, blackKeysHighlightsCanvas);
      }
      break;
  }

  return {sheetMusicNotesCurrentlyBeingPlayed, notesCurrentlySelected};
}

function handleTouchStart(e: React.TouchEvent, pianoKeys: IPianoKey[], playerNotesWhiteKeysHighlightsCanvas: HTMLCanvasElement, touchNotesBeingPlayed: ITouchIdToMidiNoteHash){
  logger.deviceKeyboard.debug({message: `touch start`});
  for(let i = 0; i < e.changedTouches.length; ++i){
    const changedTouch = e.changedTouches[i];
    const hitKey = determineKeyThatWasPressedByClientXandY(changedTouch.clientX, changedTouch.clientY, playerNotesWhiteKeysHighlightsCanvas, pianoKeys);
    if(hitKey){
      clearAnyTouchesWithSameMidiNumber(touchNotesBeingPlayed, hitKey.midiNumber);
      touchNotesBeingPlayed[changedTouch.identifier] = hitKey.midiNumber;
      emitUiKeyboardNote_pressed(hitKey.midiNumber);
    }
  }
}

function handleTouchMove(e: React.TouchEvent, pianoKeys: IPianoKey[], playerNotesWhiteKeysHighlightsCanvas: HTMLCanvasElement, touchNotesBeingPlayed: ITouchIdToMidiNoteHash){
  for(let i = 0; i < e.changedTouches.length; ++i){
    const changedTouch = e.changedTouches[i];
    const hitKey = determineKeyThatWasPressedByClientXandY(changedTouch.clientX, changedTouch.clientY, playerNotesWhiteKeysHighlightsCanvas, pianoKeys);
    if(hitKey){
      //if the move still resides on the same key, don't do anything.
      if(touchNotesBeingPlayed[changedTouch.identifier] === hitKey.midiNumber) { continue; }
      //if we made it here then the move has gone from one key to another.  Stop playing the last midi number/previous key.
      const lastMidiNumber = touchNotesBeingPlayed[changedTouch.identifier];
      clearAnyTouchesWithSameMidiNumber(touchNotesBeingPlayed, lastMidiNumber);
      //play the note that the move landed on.
      touchNotesBeingPlayed[changedTouch.identifier] = hitKey.midiNumber;
      emitUiKeyboardNote_pressed(hitKey.midiNumber);
    }
  }
}

function handleTouchEnd(e: React.TouchEvent, pianoKeys: IPianoKey[], playerNotesWhiteKeysHighlightsCanvas: HTMLCanvasElement, touchNotesBeingPlayed: ITouchIdToMidiNoteHash){
  for(let i = 0; i < e.changedTouches.length; ++i){
    const changedTouch = e.changedTouches[i];
    const midiNumber = touchNotesBeingPlayed[changedTouch.identifier];
    clearAnyTouchesWithSameMidiNumber(touchNotesBeingPlayed, midiNumber);
  }
}

function clearAnyTouchesWithSameMidiNumber(activeTouches: ITouchIdToMidiNoteHash, midiNumberParam: number){
  for(const [touchId, midiNumber] of Object.entries(activeTouches)){
    if(midiNumber === midiNumberParam){
      emitUiKeyboardNote_released(midiNumber);
      // @ts-ignore number key.
      delete activeTouches[touchId];
    }
  }
}

function handleMouseDown(e: React.MouseEvent<HTMLDivElement>, pianoKeys: IPianoKey[], playerNotesWhiteKeysHighlightsCanvas: HTMLCanvasElement, activeMouseDownMidiNumbers: Set<number>){
  logger.deviceKeyboard.info({message: `mouse down`});
  const hitKey = determineKeyThatWasPressedByClientXandY(e.clientX, e.clientY, playerNotesWhiteKeysHighlightsCanvas, pianoKeys);
  if(hitKey){
    activeMouseDownMidiNumbers.add(hitKey.midiNumber);
    emitUiKeyboardNote_pressed(hitKey.midiNumber);
  }
}

function handleMouseUp(playerNotesWhiteKeysHighlightsCanvas: HTMLCanvasElement, pianoKeys: IPianoKey[], activeMouseDownMidiNumbers: Set<number>){
  const midiNotesBeingPlayed = Array.from(activeMouseDownMidiNumbers);
  activeMouseDownMidiNumbers.clear();
  midiNotesBeingPlayed.forEach(midiNumber => emitUiKeyboardNote_released(midiNumber));
}

function determineKeyThatWasPressedByClientXandY(clientX: number, clientY: number, canvas: HTMLCanvasElement, pianoKeys: IPianoKey[]){
  const {x, y} = getRelativeXandY(clientX, clientY, canvas);
  return findPianoKeyAtXandYcoordinates(x, y, pianoKeys);
}

function getRelativeXandY(clientX: number, clientY: number, canvas: HTMLCanvasElement){
  const rectangle = canvas.getBoundingClientRect();
  const {left, top,} = rectangle;

  const x = clientX - left;
  const y = clientY - top;
  return {x, y};
}

/**
 * Hit detection is made simpler by evaluating whether black key was hit before evaluating white key.
 */
function findPianoKeyAtXandYcoordinates(x: number, y: number, pianoKeys: IPianoKey[]){
  const hitBlackKey = pianoKeys.filter(p => p.keyColor === 'black').find(p => doesXandYintersectWithKey(x, y, p));
  if(hitBlackKey){ return hitBlackKey; }
  return pianoKeys.filter(p => p.keyColor === 'white').find(p => doesXandYintersectWithKey(x, y, p));
}

/**
 * Divide by window.devicePixelRatio because it is used in setCanvasSizes (to fix blur) and makes the keys think that the width of the canvas is 2048, where mouse/touch events think it's 1024
 */
function doesXandYintersectWithKey(x: number, y: number, pianoKey: IPianoKey){
  const {topLeft: {x: topLeftX, y: topLeftY}, bottomRight: {x: bottomRightX, y: bottomRightY}} = pianoKey.coordinates;
  return (x > topLeftX / window.devicePixelRatio && x < bottomRightX / window.devicePixelRatio && y > topLeftY / window.devicePixelRatio && y < bottomRightY / window.devicePixelRatio);
}

function drawPlayerNoteHighlights(notesCurrentlyBeingPlayedByUser: Set<number>, pianoKeys: IPianoKey[], playerNotesWhiteKeysHighlightsCanvas: HTMLCanvasElement, playerNotesBlackKeysHighlightsCanvas: HTMLCanvasElement){
  requestAnimationFrame(()=>{
    const whiteKeyContext = playerNotesWhiteKeysHighlightsCanvas.getContext('2d');
    const blackKeyContext = playerNotesBlackKeysHighlightsCanvas.getContext('2d');
    if(!whiteKeyContext || !blackKeyContext) { return; }
    //clear everything out
    whiteKeyContext.clearRect(0, 0, playerNotesWhiteKeysHighlightsCanvas.width, playerNotesWhiteKeysHighlightsCanvas.height);
    blackKeyContext.clearRect(0, 0, playerNotesBlackKeysHighlightsCanvas.width, playerNotesBlackKeysHighlightsCanvas.height);
    //repaint
    const pianoKeysBeingPlayedByUser = findPianoKeysCurrentlyBeingPlayedByMidiNumber(notesCurrentlyBeingPlayedByUser, pianoKeys);
    const {whiteKeys, blackKeys} = separateWhiteAndBlackKeys(pianoKeysBeingPlayedByUser);

    drawKeysHighlights(whiteKeys, whiteKeyContext, true);
    drawKeysHighlights(blackKeys, blackKeyContext, true);
  });

}

function drawKeyAndLabelAndHighlightCanvases(
  whiteKeysHighlightsCanvas: HTMLCanvasElement,
  whiteKeysImagesCanvas: HTMLCanvasElement,
  blackKeysHighlightsCanvas: HTMLCanvasElement,
  blackKeysImagesCanvas: HTMLCanvasElement,
  sheetMusicNotesCurrentlyBeingPlayed: Note[],
  _notesCurrentlySelected: Note[],
  pianoKeys: IPianoKey[]
){
  const {whiteKeys, blackKeys} = separateWhiteAndBlackKeys(pianoKeys);

  //draw temp white and black keys while image loads:
  createContextAndDrawKeysAndLabels(whiteKeysImagesCanvas, whiteKeys);
  createContextAndDrawKeysAndLabels(blackKeysImagesCanvas, blackKeys);
  //once images have loaded, rerender.
  createImageElementsAndWaitForThemToLoad({pianoKeys}).then(()=>{
    createContextAndDrawKeysAndLabels(whiteKeysImagesCanvas, whiteKeys);
    createContextAndDrawKeysAndLabels(blackKeysImagesCanvas, blackKeys);
  });

  drawWhiteAndBlackKeysHighlightsCanvas(whiteKeysHighlightsCanvas, blackKeysHighlightsCanvas, sheetMusicNotesCurrentlyBeingPlayed, pianoKeys);
}

function onSheetMusicNotesCurrentlyBeingPlayed_changed(pianoKeys: IPianoKey[], sheetMusicNotesCurrentlyBeingPlayed: Note[], whiteKeysHighlightsCanvas: HTMLCanvasElement, blackKeysHighlightsCanvas: HTMLCanvasElement){
  requestAnimationFrame(()=>{
    drawWhiteAndBlackKeysHighlightsCanvas(whiteKeysHighlightsCanvas, blackKeysHighlightsCanvas, sheetMusicNotesCurrentlyBeingPlayed, pianoKeys);
  });
}

function onNotesCurrentlySelected_changed(pianoKeys: IPianoKey[], notesCurrentlySelected: Note[], whiteKeysHighlightsCanvas: HTMLCanvasElement, blackKeysHighlightsCanvas: HTMLCanvasElement){
  requestAnimationFrame(()=> {
    drawWhiteAndBlackKeysHighlightsCanvas(whiteKeysHighlightsCanvas, blackKeysHighlightsCanvas, notesCurrentlySelected, pianoKeys);
  });
}

function listenForWindowResizeAndOrientationChangeEvents(handle: ()=> void){
  window.addEventListener('resize', handle);
  window.addEventListener("orientationchange", handle);
  return () => {
    window.removeEventListener('resize', handle);
    window.removeEventListener('orientationchange', handle);
  };
}

/**
 * The canvas element has width and height properties that we are required to set. It is easiest to control size via CSS.
 * This function determines the CSS derived width and height, and sets the canvas element's width & height properties accordingly.
 * @param canvases
 */
function setCanvasSizesToMatchCSS(canvases: HTMLCanvasElement[]){
  //control canvas size entirely through CSS.
  for(const canvas of canvases){
    setCanvasSizeToMatchCSS(canvas);
  }
}

/**
 * Canvas uses 2 distinct sets of height and width.
 * The first, which is set by attribute only, is the pixel width.
 * The second, which is set by CSS, is the display width.
 * We want these to match, but leave control of styling in the CSS world.
 * https://stackoverflow.com/questions/10214873/make-canvas-as-wide-and-as-high-as-parent
 *
 * We also need to factor in window.devicePixelRatio, so that our canvas isn't blurry.
 */
function setCanvasSizeToMatchCSS(canvas: HTMLCanvasElement){
  const baseWidth = window.innerWidth;
  let baseHeight;

  // Set the keyboard aspect ratio based on the viewport width,
  // setting the breakpoint based on a phone in landscape orientation
  if (baseWidth > 666) {
    baseHeight = baseWidth / 7.77;
  } else {
    baseHeight = baseWidth / 4.5;
  }

  canvas.width  = baseWidth * window.devicePixelRatio; //https://stackoverflow.com/questions/24332639/why-context2d-backingstorepixelratio-deprecated
  canvas.height = baseHeight * window.devicePixelRatio;
  return {height: canvas.height, width: canvas.width};
}

function createContextAndDrawKeysAndLabels(backgroundCanvas: HTMLCanvasElement, pianoKeys: IPianoKey[]){
  const ctx = backgroundCanvas.getContext('2d');
  if(!ctx) { return; }
  drawKeys(pianoKeys, ctx);
  drawNoteNameLabels(pianoKeys, ctx);
}

function drawWhiteAndBlackKeysHighlightsCanvas(whiteKeysHighlightsCanvas: HTMLCanvasElement, blackKeysHighlightsCanvas: HTMLCanvasElement, sheetMusicNotesCurrentlyBeingPlayed: Note[], pianoKeys: IPianoKey[]){
  const whiteKeysCtx = whiteKeysHighlightsCanvas.getContext('2d');
  const blackKeysCtx = blackKeysHighlightsCanvas.getContext('2d');
  if(!whiteKeysCtx){return;}
  if(!blackKeysCtx){return;}
  //clear the canvas first.
  whiteKeysCtx.clearRect(0, 0, whiteKeysHighlightsCanvas.width, whiteKeysHighlightsCanvas.height);
  blackKeysCtx.clearRect(0, 0, blackKeysHighlightsCanvas.width, blackKeysHighlightsCanvas.height);

  //paint the canvas.
  const pianoKeysForSheetMusicNotesCurrentlyBeingPlayed = findPianoKeysCurrentlyBeingPlayed(sheetMusicNotesCurrentlyBeingPlayed, pianoKeys);
  const {whiteKeys, blackKeys} = separateWhiteAndBlackKeys(pianoKeysForSheetMusicNotesCurrentlyBeingPlayed);

  drawKeysHighlights(whiteKeys, whiteKeysCtx, false);
  drawKeysHighlights(blackKeys, blackKeysCtx, false);
}

function separateWhiteAndBlackKeys(pianoKeys: IPianoKey[]){
  const whiteKeys: IPianoKey[] = [];
  const blackKeys: IPianoKey[] = [];
  for(let i = 0; i < pianoKeys.length; ++i){
    const pianoKey = pianoKeys[i];
    if(pianoKey.keyColor === 'white'){
      whiteKeys.push(pianoKey);
    }else{
      blackKeys.push(pianoKey);
    }
  }
  return {whiteKeys, blackKeys};
}

function findPianoKeysCurrentlyBeingPlayed(notesCurrentlyBeingPlayed: Note[], pianoKeys: IPianoKey[]){
  const result: IPianoKey[] = [];
  for(let i = 0; i < notesCurrentlyBeingPlayed.length; ++i){
    const note = notesCurrentlyBeingPlayed[i];
    //if the note is part of a tie (ie has a tie begin), do not include it so that it is not highlighted twice
    if(note.tieBeginNote){
      continue;
    }
    const pianoKey = pianoKeys.find(p => p.midiNumber === note.midi);
    if(pianoKey){
      pianoKey.hand = note.hand === "left hand" ? "left" : "right";
      result.push(pianoKey);
    }
  }
  return result;
}

function findPianoKeysCurrentlyBeingPlayedByMidiNumber(midiNumbersCurrentlyBeingPlayed: Set<number>, pianoKeys: IPianoKey[]){
  const result: IPianoKey[] = [];
  const midiNoteArray = Array.from(midiNumbersCurrentlyBeingPlayed);
  for(let i = 0; i < midiNoteArray.length; ++i){
    const midiNote = midiNoteArray[i];
    const pianoKey = pianoKeys.find(p => p.midiNumber === midiNote);
    if(pianoKey){
      result.push(pianoKey);
    }
  }
  return result;
}
